/**
 * KitsStore Context - Offline-first store for AI Creations.
 * Hydrates from browser cache immediately, then syncs with backend.
 * Acts as a lightweight "store" (Redux-like) for kits data.
 */

import React, { createContext, useContext, useCallback, useMemo, useState, useEffect } from 'react';
import { getKitsFromCache, saveKitsToCache, mergeKitsIntoCache, clearKitsCache, isCacheStale } from '../store/kitsCache';

const KitsStoreContext = createContext(null);

export function KitsStoreProvider({ children }) {
  const [cachedKits, setCachedKits] = useState([]);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [hydratedFromCache, setHydratedFromCache] = useState(false);

  // Hydrate from cache on mount (cache-first)
  useEffect(() => {
    const fromCache = getKitsFromCache();
    if (fromCache?.kits?.length) {
      setCachedKits(fromCache.kits);
      setHydratedFromCache(true);
    }
  }, []);

  // Listen for online/offline
  useEffect(() => {
    const onOnline = () => setIsOffline(false);
    const onOffline = () => setIsOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const setKits = useCallback((kitsOrUpdater) => {
    setCachedKits(prev => {
      const next = typeof kitsOrUpdater === 'function' ? kitsOrUpdater(prev) : kitsOrUpdater;
      if (Array.isArray(next)) {
        saveKitsToCache({ kits: next, pagination: { totalKits: next.length } }, 10);
      }
      return next;
    });
  }, []);

  const updateFromFetch = useCallback((kits, pagination, pageSize = 10, isRefresh = false) => {
    if (isRefresh) {
      setCachedKits(kits);
      saveKitsToCache({ kits, pagination }, pageSize);
    } else {
      setCachedKits(prev => {
        const ids = new Set(prev.map(k => k._id));
        const merged = [...prev];
        for (const k of kits || []) {
          if (k._id && !ids.has(k._id)) {
            merged.push(k);
            ids.add(k._id);
          }
        }
        mergeKitsIntoCache(kits, pagination, pageSize);
        return merged;
      });
    }
  }, []);

  const clearCache = useCallback(() => {
    clearKitsCache();
    setCachedKits([]);
    setHydratedFromCache(false);
  }, []);

  const value = useMemo(() => ({
    cachedKits,
    setCachedKits: setKits,
    updateFromFetch,
    isOffline,
    hydratedFromCache,
    getCachedKits: getKitsFromCache,
    isCacheStale,
    clearCache
  }), [cachedKits, setKits, updateFromFetch, isOffline, hydratedFromCache]);

  return (
    <KitsStoreContext.Provider value={value}>
      {children}
    </KitsStoreContext.Provider>
  );
}

export function useKitsStore() {
  const ctx = useContext(KitsStoreContext);
  if (!ctx) {
    return null; // Optional - App may not be wrapped
  }
  return ctx;
}
